export const navLinks = [
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "Contact Us", to: "/contact" },
];

export const proofPoints = [
  [
    "01",
    "One accountable team",
    "The Bridge manages execution internally in this phase, so every handoff has a clear owner.",
  ],
  [
    "02",
    "Milestone visibility",
    "Know what has happened, what happens next and what is needed from you.",
  ],
  [
    "03",
    "Built for distance",
    "A dependable operating layer for diaspora clients, local customers and growing teams.",
  ],
];

export const testimonials = [
  {
    quote:
      "The value is not just finding an option. It is having one team keep the details moving until the work is complete.",
    name: "Amina O.",
    role: "Diaspora customer",
    initials: "AO",
  },
  {
    quote:
      "We could finally see what was outstanding, what had been paid and who owned the next step. That changed the experience completely.",
    name: "Tunde M.",
    role: "SME founder",
    initials: "TM",
  },
  {
    quote:
      "The Bridge gives our team a practical local execution partner without losing the visibility our board expects.",
    name: "Chidi N.",
    role: "Operations lead",
    initials: "CN",
  },
  {
    quote:
      "I could make decisions from abroad because the updates were clear and the evidence was always attached to the work.",
    name: "Nneka E.",
    role: "International customer",
    initials: "NE",
  },
  {
    quote:
      "The process felt deliberate from the first brief to the final handover. That level of ownership is rare.",
    name: "Kola A.",
    role: "Business owner",
    initials: "KA",
  },
  {
    quote:
      "It gave our leadership team confidence that an important local project was not disappearing into a chain of handoffs.",
    name: "Sarah I.",
    role: "Programme director",
    initials: "SI",
  },
];

export const faqs = [
  {
    question: "What can The Bridge handle for me?",
    answer:
      "We coordinate automobile sourcing, logistics, clearing and forwarding, property services, concierge support, procurement management and regulatory compliance for individuals and organisations that need dependable execution in Nigeria.",
  },
  {
    question: "Who is responsible for carrying out my request?",
    answer:
      "The Bridge remains accountable for the work from initial review to handover. Your request has an operational owner who coordinates the people, documents, approvals and milestones needed to complete it.",
  },
  {
    question: "Will I know the cost before work begins?",
    answer:
      "Yes. After reviewing your requirements, we provide a clear scope and pricing for your approval. Where a service has stages, the related payment milestones and dependencies are shown before execution moves forward.",
  },
  {
    question: "How do I follow progress when I am not in Nigeria?",
    answer:
      "Your customer portal brings the request status, milestones, messages, documents, approvals and completion evidence together. Notifications also tell you when a decision, document or payment is needed from you.",
  },
  {
    question: "How are payments and sensitive documents handled?",
    answer:
      "Approved payment options are presented inside your account, and service documents are exchanged through the secure document centre. Avoid sending payment credentials or sensitive records through unprotected channels.",
  },
  {
    question: "What happens if more information is needed?",
    answer:
      "The operational team will explain exactly what is missing and why it matters. Depending on the service, this may include identification, specifications, invoices, property papers, shipping records, approvals or delivery details.",
  },
];

export const posts = [
  {
    slug: "a-better-way-to-execute-in-nigeria",
    category: "The Bridge journal",
    date: "August 12, 2026",
    title: "A better way to execute in Nigeria from anywhere",
    excerpt:
      "Distance should not mean uncertainty. Here is the operating model we are building for customers who need local action and global visibility.",
    readTime: "4 min read",
  },
  {
    slug: "the-five-checks-before-buying-a-car-abroad",
    category: "Automobiles",
    date: "July 28, 2026",
    title: "The five checks before buying a car abroad",
    excerpt:
      "A practical guide to moving from a promising listing to a purchase decision you can defend.",
    readTime: "6 min read",
  },
  {
    slug: "what-milestone-payments-protect",
    category: "Trust & operations",
    date: "July 06, 2026",
    title: "What milestone payments protect",
    excerpt:
      "Why a visible delivery plan matters as much as the amount on an invoice.",
    readTime: "5 min read",
  },
];

export const legalPolicies = {
  privacy: {
    title: "Privacy policy",
    eyebrow: "Legal",
    intro:
      "This draft explains how The Bridge by VA may collect, use and protect information when you visit our website, create an account, submit a service request or communicate with our team. It is written for product review and will be replaced with counsel-approved language before launch.",
    updated: "Draft for legal review — last reviewed August 2026",
    sections: [
      [
        "1. Scope and responsibility",
        "This policy applies to the public website and the Bridge customer experience, including account registration, service requests, document exchange, payment coordination, status updates and support. The Bridge by VA is the operating party responsible for deciding why and how customer information is used. The final version will identify the legal entity, registered address and privacy contact confirmed by counsel.",
      ],
      [
        "2. Information we collect",
        "Depending on how you use the service, we may collect your name, email address, phone number, country or city, organisation details, account credentials, identity or verification information, service requirements, delivery details, uploaded documents and images, payment references, invoices, messages, support requests and feedback. We may also receive technical information such as IP address, device and browser details, login timestamps, approximate location, cookie identifiers and security events. Please do not submit information that is not needed for your request.",
      ],
      [
        "3. How we use information",
        "We use information to create and verify accounts; understand and scope service requests; coordinate execution, documents and milestones; provide quotes, payment instructions and receipts; send account, security and progress notifications; answer questions; prevent fraud, abuse and unauthorised access; maintain audit trails; improve reliability and user experience; and comply with legal, regulatory or contractual obligations. We will not use sensitive information for an unrelated purpose without an appropriate basis or notice.",
      ],
      [
        "4. Sharing and disclosures",
        "We may share relevant information with our authorised internal operations, customer support, finance, compliance and technology teams so they can perform their responsibilities. We may also use carefully selected infrastructure, communications, identity, payment and analytics providers acting on our instructions. Information may be disclosed where required by law, to protect people or platform security, to investigate suspected fraud, or as part of a business transfer. The current operating phase is handled by The Bridge’s internal team; any future service-provider network will be covered by an updated notice before it is opened.",
      ],
      [
        "5. Documents, payments and international transfers",
        "Service requests may require identity, property, shipping, vehicle, procurement or compliance records. Upload only authentic documents that you are authorised to share, and use the secure workspace rather than sending sensitive records through an unprotected channel. Payment details may be processed by payment partners; The Bridge does not intend to store complete card numbers. Where information is transferred across borders, the final policy will state the safeguards and lawful transfer mechanism selected for the production providers.",
      ],
      [
        "6. Retention and security",
        "We retain information for as long as it is reasonably needed to operate your account, complete or evidence a service, resolve disputes, meet accounting or compliance duties, enforce agreements and protect the platform. Retention periods will be documented in the final schedule. We use access controls, encrypted connections, secure password storage, audit logging, backups and monitoring designed to reduce avoidable risk; no internet service can guarantee absolute security. Contact us promptly if you believe an account or document has been compromised.",
      ],
      [
        "7. Your rights and contact",
        "Subject to applicable law, you may ask for a copy of your information, request correction, object to or restrict certain processing, request deletion where retention is not required, or withdraw consent where consent is the basis. We may need to verify your identity before acting. The final policy will publish the dedicated privacy contact, response times, complaint route and any jurisdiction-specific rights.",
      ],
    ],
  },
  terms: {
    title: "Terms of service",
    eyebrow: "Legal",
    intro:
      "These draft terms describe the basic rules for using The Bridge by VA to request, coordinate and track services in Nigeria. They are product-stage content only and must be reviewed, completed and approved by counsel before public launch.",
    updated: "Draft for legal review — last reviewed August 2026",
    sections: [
      [
        "1. Agreement and eligibility",
        "By using the website or customer workspace, you agree to these terms and any order-specific terms shown to you. You must be legally able to enter a binding agreement and provide accurate account information. If you use the service for a business, you confirm that you are authorised to bind that organisation. The final version will define the minimum age, entity details and governing law.",
      ],
      [
        "2. Accounts and account security",
        "Registration may require an email address or phone number, contact verification, a strong password and profile information. Keep credentials private, use a unique password and notify us immediately of suspected unauthorised access. You are responsible for activity performed through your account unless applicable law provides otherwise. We may require additional verification before sensitive actions or document access.",
      ],
      [
        "3. Service requests and scope",
        "You may select an available category and submit requirements, files, timing and delivery details. We rely on the information you provide to assess scope, risk, timing and price, so tell us promptly when it changes. A request is not a promise that a service can be completed. It becomes an accepted order only when The Bridge confirms the scope, applicable terms, payment requirements and next milestone.",
      ],
      [
        "4. Operations and communications",
        "The Bridge currently manages execution through its internal operations team. We may ask for documents, clarifications, approvals or access needed to perform the agreed work. Statuses, messages, evidence and milestone dates are provided to support informed decisions, but estimated dates can move where dependencies, authorities, weather, third parties or information gaps affect delivery. Keep contact details current and review notifications promptly.",
      ],
      [
        "5. Pricing, payments and milestones",
        "The customer workspace will show the applicable price, taxes or charges, payment method, milestone schedule and any specific cancellation or refund terms before approval. Available methods may include cards, bank transfer and international payment flows through approved processors. Do not send payment credentials through chat or email. Work may pause where a required payment, approval or document is outstanding. The final terms will state treatment of failed payments, refunds, chargebacks and currency conversion.",
      ],
      [
        "6. Documents and customer responsibilities",
        "You must have the right to provide every document, image, instruction, address and personal detail submitted through the service. Do not upload malware, confidential information belonging to another party without permission, forged records or content that infringes another person’s rights. You are responsible for reviewing deliverables, raising an issue through the support channel and meeting actions assigned to you.",
      ],
      [
        "7. Availability, suspension and termination",
        "We may maintain, change or temporarily suspend parts of the website or workspace for security, maintenance, legal or operational reasons. We may restrict or close an account where information is materially false, payment is abusive, the service is misused, security is at risk or the relationship cannot lawfully continue. We will preserve records and complete any surviving payment, confidentiality, dispute or compliance obligations as required.",
      ],
      [
        "8. Disclaimers, limits and updates",
        "The Bridge coordinates execution and provides operational visibility; it does not guarantee that every requested service, authority decision, market condition or third-party action will occur on a particular date. To the extent permitted by law, the final terms will describe excluded warranties, liability limits, dispute procedures, force-majeure events and intellectual-property ownership. We may update these terms by publishing a new version and, where required, providing notice.",
      ],
    ],
  },
  cookies: {
    title: "Cookie policy",
    eyebrow: "Legal",
    intro:
      "This draft explains how The Bridge by VA may use cookies, local storage and similar technologies on the public website. The final policy will identify the production tools, providers, retention periods and consent controls after legal and product review.",
    updated: "Draft for legal review — last reviewed August 2026",
    sections: [
      [
        "1. What cookies are",
        "Cookies are small text files placed on a browser or device. Similar technologies can remember a session, store a preference or help us understand whether a page is working. A cookie may be set by The Bridge or by a service provider acting for us, and may be temporary or remain until it expires or is deleted.",
      ],
      [
        "2. Essential technologies",
        "We may use strictly necessary storage for security controls, session continuity, consent preferences, form protection, accessibility choices and reliable navigation. These technologies support a service you have requested and may not be switchable through a consent banner. The final policy will list each essential technology and its retention period.",
      ],
      [
        "3. Analytics and experience",
        "With the appropriate consent, we may use privacy-conscious analytics to understand page performance, traffic patterns, device compatibility and which content helps visitors. We may also remember preferences such as a dismissed banner or language choice. Analytics should be configured to minimise direct identification and will be disabled or limited where consent is withdrawn.",
      ],
      [
        "4. Third-party services",
        "Embedded media, payment handoffs, customer support, security, fonts or measurement tools may set their own technologies when used. The production cookie list will name these providers, explain their purpose and link to their policies. We do not authorise third parties to use Bridge data for unrelated advertising purposes through our website.",
      ],
      [
        "5. Your controls",
        "You can manage cookies through the consent controls we provide and through your browser or device settings. Blocking essential storage may affect sign-in, security or form functionality. Deleting cookies does not necessarily delete account information held in our systems. The final release will document how to revisit consent and how long consent records are retained.",
      ],
      [
        "6. Updates and contact",
        "We may update this policy when the website, technology providers or legal requirements change. The date above will be refreshed when a new version is published. Questions about cookies can be sent through the contact route published in the final policy.",
      ],
    ],
  },
  "acceptable-use": {
    title: "Acceptable use policy",
    eyebrow: "Legal",
    intro:
      "The Bridge by VA is designed for accountable service execution. This draft sets clear expectations for using our website, account, customer workspace, documents, messages and operational workflows safely and respectfully.",
    updated: "Draft for legal review — last reviewed August 2026",
    sections: [
      [
        "1. Permitted use",
        "Use the platform to learn about available services, create and manage your own account, submit genuine service requirements, exchange relevant records, approve authorised milestones, make legitimate payments and communicate with the Bridge team about an active request. Use only the accounts, documents and information you are authorised to access.",
      ],
      [
        "2. Prohibited conduct",
        "You must not provide materially false, misleading or forged information; impersonate a person or organisation; create accounts for abuse or evasion; submit a request for an unlawful purpose; upload malware, ransomware, spyware or harmful code; probe, scan or bypass security controls; access another user’s account or records; scrape or overload the service; interfere with delivery; manipulate payments, milestones or evidence; infringe intellectual property or privacy rights; or use the platform to harass, threaten, discriminate or deceive.",
      ],
      [
        "3. Files, data and communications",
        "Only upload files that are relevant, safe and lawfully obtained. Keep personal, financial, identity and commercial information to the minimum needed for the request. Messages should be clear, professional and related to the work. We may scan files, apply security controls, moderate content and retain audit records needed to protect users and investigate incidents.",
      ],
      [
        "4. Reporting a concern",
        "If you notice suspicious access, a malicious file, a fraudulent request, a privacy issue or a message that puts someone at risk, report it promptly through the support or contact route shown on the website. Include enough context to investigate, but do not forward passwords, full payment credentials or unnecessary sensitive records.",
      ],
      [
        "5. Enforcement",
        "We may warn, remove content, require additional verification, pause a request, restrict features, suspend or close an account, preserve records, reverse access or contact relevant authorities where misuse or a security concern is suspected. We will apply appropriate review and notice processes where required by law. Nothing in this draft limits urgent action needed to protect people, systems or evidence.",
      ],
      [
        "6. Policy changes",
        "We may revise this policy as services, threats and legal requirements change. Continued use after an effective update means you acknowledge the revised expectations, subject to any notice or consent required by law. The final version will state the effective date and governing framework.",
      ],
    ],
  },
};
